import "./program/conditionalProgramTabs.css";
import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { QRCodeSVG } from "qrcode.react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  QrCode,
  Printer,
  Download,
  Calendar,
  Clock,
  MapPin,
  Trophy,
  CheckCircle,
  Loader2,
  FileText,
  Grid3X3,
  List,
  Users,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const GameCards = ({ program, onUpdate }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [games, setGames] = useState([]);
  const [viewMode, setViewMode] = useState("grid"); // grid or list
  const [selectedGame, setSelectedGame] = useState(null);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [selectedGamesForPrint, setSelectedGamesForPrint] = useState([]);
  const printRef = useRef(null);

  const baseUrl = window.location.origin;

  useEffect(() => {
    fetchGames();
  }, [program?.program_id]);

  const fetchGames = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(
        `${API}/programs/${program.program_id}/games`,
        { headers }
      );
      setGames(response.data.games || []);
    } catch (error) {
      console.error("Error fetching games:", error);
      setLoadError("Unable to load game cards. Try again.");
      toast.error("Failed to load games");
    } finally {
      setLoading(false);
    }
  };

  const getScoreUrl = (gameCode) => {
    return `${baseUrl}/score/${gameCode}`;
  };

  const handlePrint = () => {
    const printContent = document.getElementById("print-area");
    const newWindow = window.open("", "_blank");
    newWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Game Cards - ${program.name}</title>
          <style>
            @page { margin: 0.5in; }
            body { font-family: system-ui, -apple-system, sans-serif; }
            .game-card {
              border: 2px solid #000;
              border-radius: 12px;
              padding: 16px;
              margin-bottom: 24px;
              page-break-inside: avoid;
              max-width: 400px;
            }
            .card-header {
              text-align: center;
              border-bottom: 1px solid #e5e7eb;
              padding-bottom: 12px;
              margin-bottom: 12px;
            }
            .program-name { font-size: 14px; color: #6b7280; margin-bottom: 4px; }
            .game-title { font-size: 18px; font-weight: bold; margin: 0; }
            .teams {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin: 16px 0;
            }
            .team {
              text-align: center;
              flex: 1;
            }
            .team-label { font-size: 10px; color: #9ca3af; text-transform: uppercase; }
            .team-name { font-size: 16px; font-weight: 600; }
            .vs { color: #9ca3af; font-weight: bold; }
            .qr-section {
              text-align: center;
              margin: 16px 0;
              padding: 16px;
              background: #f9fafb;
              border-radius: 8px;
            }
            .qr-code { margin: 0 auto; }
            .qr-instruction { font-size: 12px; color: #4b5563; margin-top: 8px; }
            .game-details {
              display: flex;
              justify-content: space-around;
              padding-top: 12px;
              border-top: 1px solid #e5e7eb;
              font-size: 12px;
              color: #6b7280;
            }
            .detail-item { text-align: center; }
            .print-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 24px;
            }
            @media print {
              .game-card { break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    newWindow.document.close();
    newWindow.print();
  };

  const toggleGameSelection = (gameCode) => {
    setSelectedGamesForPrint(prev => 
      prev.includes(gameCode) 
        ? prev.filter(g => g !== gameCode)
        : [...prev, gameCode]
    );
  };

  const selectAllGames = () => {
    if (selectedGamesForPrint.length === games.length) {
      setSelectedGamesForPrint([]);
    } else {
      setSelectedGamesForPrint(games.map(g => g.game_code));
    }
  };

  const gamesToPrint = selectedGamesForPrint.length > 0 
    ? games.filter(g => selectedGamesForPrint.includes(g.game_code))
    : games;

  if (loading) {
    return (
      <div role="status" aria-live="polite" className="program-conditional flex items-center justify-center gap-3 py-8">
        <Loader2 className="w-5 h-5 animate-spin" />
        Loading game cards…
      </div>
    );
  }

  if (loadError) {
    return <div role="alert" className="program-conditional program-data-panel">
      <p>{loadError}</p>
      <Button variant="outline" onClick={fetchGames}>Try again</Button>
    </div>;
  }

  if (games.length === 0) {
    return (
      <div className="program-section program-conditional program-empty-state" data-testid="game-cards-empty">
        <QrCode className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-slate-900 mb-2">No Games Found</h3>
        <p className="text-slate-500 mb-4">
          Generate a schedule or bracket first to create game cards.
        </p>
        <p className="text-sm text-slate-400">
          Game cards will automatically appear once you have a schedule or tournament bracket.
        </p>
      </div>
    );
  }

  return (
    <div className="program-section program-conditional program-game-cards" data-testid="game-cards">
      {/* Header */}
      <div className="program-section-heading">
        <div>
          <h2 className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-indigo-600" />
            Game Cards
          </h2>
          <p className="text-sm text-slate-500">
            {games.length} games • {games.filter(g => g.has_result).length} completed
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center border border-slate-200/60 rounded-lg p-1">
            <button
              aria-label="Grid view" aria-pressed={viewMode === "grid"}
              onClick={() => setViewMode("grid")}
              className={`p-2 rounded ${viewMode === "grid" ? "bg-slate-100" : ""}`}
            >
              <Grid3X3 className="w-4 h-4" />
            </button>
            <button
              aria-label="List view" aria-pressed={viewMode === "list"}
              onClick={() => setViewMode("list")}
              className={`p-2 rounded ${viewMode === "list" ? "bg-slate-100" : ""}`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowPrintDialog(true)}
            data-testid="print-cards-btn"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print Cards
          </Button>
        </div>
      </div>

      {/* Games Grid/List */}
      <div className={viewMode === "grid" 
        ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        : "space-y-3"
      }>
        {games.map((game) => (
          <div
            key={game.game_code}
            className={`conditional-game-card bg-white border overflow-hidden cursor-pointer ${
              game.has_result ? "border-green-300" : "border-slate-200/60"
            }`}
            role="button"
            tabIndex={0}
            aria-label={`View game ${game.game_id}: ${game.home_team} vs ${game.away_team}`}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setSelectedGame(game);
              }
            }}
            onClick={() => setSelectedGame(game)}
            data-testid={`game-card-${game.game_id}`}
          >
            {viewMode === "grid" ? (
              // Grid View
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <Badge variant="outline" className="text-xs">
                    {game.week ? `Week ${game.week}` : game.round || game.game_id}
                  </Badge>
                  {game.has_result && (
                    <Badge className="bg-green-100 text-green-700 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Completed
                    </Badge>
                  )}
                </div>
                
                <div className="flex items-center justify-between mb-4">
                  <div className="text-center flex-1">
                    <p className="text-xs text-slate-500 uppercase">Home</p>
                    <p className="font-semibold text-sm truncate">{game.home_team}</p>
                    {game.has_result && (
                      <p className="text-2xl font-semibold text-indigo-600">{game.result?.home_score}</p>
                    )}
                  </div>
                  <div className="px-3 text-slate-400 font-semibold">vs</div>
                  <div className="text-center flex-1">
                    <p className="text-xs text-slate-500 uppercase">Away</p>
                    <p className="font-semibold text-sm truncate">{game.away_team}</p>
                    {game.has_result && (
                      <p className="text-2xl font-semibold text-indigo-600">{game.result?.away_score}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-center gap-4 text-xs text-slate-500 border-t pt-3">
                  {game.date && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {game.date}
                    </span>
                  )}
                  {game.time && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {game.time}
                    </span>
                  )}
                </div>
                
                <div className="mt-3 flex justify-center">
                  <QRCodeSVG
                    value={getScoreUrl(game.game_code)}
                    size={80}
                    level="M"
                    className="rounded"
                  />
                </div>
              </div>
            ) : (
              // List View
              <div className="p-4 flex items-center gap-4">
                <QRCodeSVG
                  value={getScoreUrl(game.game_code)}
                  size={60}
                  level="M"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-xs">
                      {game.week ? `Week ${game.week}` : game.round || game.game_id}
                    </Badge>
                    {game.has_result && (
                      <Badge className="bg-green-100 text-green-700 text-xs">
                        Completed
                      </Badge>
                    )}
                  </div>
                  <p className="font-semibold text-sm">
                    {game.home_team} vs {game.away_team}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                    {game.date && <span>{game.date}</span>}
                    {game.time && <span>{game.time}</span>}
                    {game.venue && <span>{game.venue}</span>}
                  </div>
                </div>
                {game.has_result && (
                  <div className="text-right">
                    <p className="text-lg font-semibold">
                      {game.result?.home_score} - {game.result?.away_score}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Game Detail Modal */}
      <Dialog open={!!selectedGame} onOpenChange={() => setSelectedGame(null)}>
        <DialogContent className="program-conditional conditional-dialog max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-indigo-600" />
              {selectedGame?.game_id}
            </DialogTitle>
            <DialogDescription>Game details and the QR code for submitting a score.</DialogDescription>
          </DialogHeader>
          
          {selectedGame && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-center flex-1">
                    <p className="text-xs text-slate-500 uppercase mb-1">Home</p>
                    <p className="font-semibold text-lg">{selectedGame.home_team}</p>
                    {selectedGame.has_result && (
                      <p className="text-3xl font-semibold text-indigo-600 mt-2">
                        {selectedGame.result?.home_score}
                      </p>
                    )}
                  </div>
                  <div className="px-4 text-slate-400 font-semibold text-xl">vs</div>
                  <div className="text-center flex-1">
                    <p className="text-xs text-slate-500 uppercase mb-1">Away</p>
                    <p className="font-semibold text-lg">{selectedGame.away_team}</p>
                    {selectedGame.has_result && (
                      <p className="text-3xl font-semibold text-indigo-600 mt-2">
                        {selectedGame.result?.away_score}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center justify-center gap-4 text-sm text-slate-600 border-t pt-3">
                  {selectedGame.date && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {selectedGame.date}
                    </span>
                  )}
                  {selectedGame.time && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {selectedGame.time}
                    </span>
                  )}
                </div>
                {selectedGame.venue && (
                  <div className="flex items-center justify-center gap-1 text-sm text-slate-600 mt-2">
                    <MapPin className="w-4 h-4" />
                    {selectedGame.venue}
                  </div>
                )}
              </div>

              {/* QR Code */}
              <div className="bg-indigo-50 rounded-2xl p-6 text-center">
                <QRCodeSVG
                  value={getScoreUrl(selectedGame.game_code)}
                  size={180}
                  level="H"
                  includeMargin
                  className="mx-auto rounded-lg"
                />
                <p className="text-sm text-indigo-700 mt-4 font-medium">
                  Scan to submit score
                </p>
                <p className="text-xs text-indigo-500 mt-1 break-all">
                  {getScoreUrl(selectedGame.game_code)}
                </p>
              </div>

              {selectedGame.has_result && (
                <div className="bg-green-50 border border-green-200 rounded-2xl p-3 text-center">
                  <div className="flex items-center justify-center gap-2 text-green-700">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Score recorded</span>
                  </div>
                  {selectedGame.result?.recorded_at && (
                    <p className="text-xs text-green-600 mt-1">
                      {new Date(selectedGame.result.recorded_at).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Print Dialog */}
      <Dialog open={showPrintDialog} onOpenChange={setShowPrintDialog}>
        <DialogContent className="program-conditional conditional-dialog max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5" />
              Print Game Cards
            </DialogTitle>
            <DialogDescription>Select game cards and review the preview before printing.</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Selection Controls */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  aria-label="Select all games"
                  checked={selectedGamesForPrint.length === games.length}
                  onChange={selectAllGames}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm text-slate-600">
                  {selectedGamesForPrint.length === 0 
                    ? "All games selected"
                    : `${selectedGamesForPrint.length} of ${games.length} selected`
                  }
                </span>
              </div>
              <Button onClick={handlePrint} className="conditional-primary">
                <Printer className="w-4 h-4 mr-2" />
                Print {gamesToPrint.length} Card{gamesToPrint.length !== 1 ? "s" : ""}
              </Button>
            </div>

            {/* Selection Grid */}
            <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto p-2 border rounded-lg">
              {games.map((game) => (
                <label
                  key={game.game_code}
                  className={`flex items-center gap-2 p-2 rounded cursor-pointer text-xs ${
                    selectedGamesForPrint.includes(game.game_code) || selectedGamesForPrint.length === 0
                      ? "bg-indigo-50 border border-indigo-200"
                      : "bg-slate-50 border border-slate-200/60"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedGamesForPrint.includes(game.game_code) || selectedGamesForPrint.length === 0}
                    onChange={() => toggleGameSelection(game.game_code)}
                    className="w-3 h-3 rounded"
                  />
                  <span className="truncate">{game.game_id}</span>
                </label>
              ))}
            </div>

            {/* Print Preview */}
            <div id="print-area" className="print-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px" }}>
              {gamesToPrint.map((game) => (
                <div key={game.game_code} className="game-card" style={{
                  border: "2px solid #000",
                  borderRadius: "12px",
                  padding: "16px",
                  backgroundColor: "#fff"
                }}>
                  <div className="card-header" style={{
                    textAlign: "center",
                    borderBottom: "1px solid #e5e7eb",
                    paddingBottom: "12px",
                    marginBottom: "12px"
                  }}>
                    <p className="program-name" style={{ fontSize: "12px", color: "#6b7280", margin: "0 0 4px 0" }}>
                      {program.name}
                    </p>
                    <p className="game-title" style={{ fontSize: "16px", fontWeight: "bold", margin: 0 }}>
                      {game.week ? `Week ${game.week}` : game.round || ""} - Game {game.game_id}
                    </p>
                  </div>
                  
                  <div className="teams" style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    margin: "16px 0"
                  }}>
                    <div className="team" style={{ textAlign: "center", flex: 1 }}>
                      <p style={{ fontSize: "10px", color: "#9ca3af", textTransform: "uppercase", margin: "0 0 4px 0" }}>HOME</p>
                      <p style={{ fontSize: "14px", fontWeight: "600", margin: 0 }}>{game.home_team}</p>
                    </div>
                    <div className="vs" style={{ color: "#9ca3af", fontWeight: "bold", padding: "0 8px" }}>vs</div>
                    <div className="team" style={{ textAlign: "center", flex: 1 }}>
                      <p style={{ fontSize: "10px", color: "#9ca3af", textTransform: "uppercase", margin: "0 0 4px 0" }}>AWAY</p>
                      <p style={{ fontSize: "14px", fontWeight: "600", margin: 0 }}>{game.away_team}</p>
                    </div>
                  </div>

                  <div className="qr-section" style={{
                    textAlign: "center",
                    margin: "16px 0",
                    padding: "16px",
                    backgroundColor: "#f9fafb",
                    borderRadius: "8px"
                  }}>
                    <QRCodeSVG
                      value={getScoreUrl(game.game_code)}
                      size={120}
                      level="H"
                      className="qr-code"
                      style={{ margin: "0 auto" }}
                    />
                    <p className="qr-instruction" style={{ fontSize: "11px", color: "#4b5563", marginTop: "8px" }}>
                      Scan to submit final score
                    </p>
                  </div>

                  <div className="game-details" style={{
                    display: "flex",
                    justifyContent: "space-around",
                    paddingTop: "12px",
                    borderTop: "1px solid #e5e7eb",
                    fontSize: "11px",
                    color: "#6b7280"
                  }}>
                    {game.date && (
                      <div className="detail-item" style={{ textAlign: "center" }}>
                        <span>{game.date}</span>
                      </div>
                    )}
                    {game.time && (
                      <div className="detail-item" style={{ textAlign: "center" }}>
                        <span>{game.time}</span>
                      </div>
                    )}
                    {game.venue && (
                      <div className="detail-item" style={{ textAlign: "center" }}>
                        <span>{game.venue}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GameCards;
